import ZTable, { IColumnType, IFilterItem, ITableRef } from '@/components/ZTable';
import { ModalWidth } from '@/config';
import { Button, Col, Form, Input, message, Modal, Row, Tooltip } from 'antd';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { PlusCircleOutlined, SaveOutlined, CheckOutlined, LockOutlined } from '@ant-design/icons';
import EnumSelect from '@/components/FormItems/EnumSelect';
import { PartnerAccountType } from '@/constants';
import i18n from '@/helpers/i18n';
import BooleanTag from '@/components/app/BooleanTag';
import { PartnerAccountDto } from '@/__generated';
import { apiSdk } from '@/httpClient';

type Props = {
  onClose: () => void;
  utilityPartnerId: string;
};

const columns: IColumnType<PartnerAccountDto>[] = [
  {
    title: 'partnerPage_Bank_OperatorName',
    dataIndex: 'name',
    ellipsis: true,
  },
  {
    title: 'partnerPage_AccountType',
    dataIndex: 'partnerAccountType',
    ellipsis: true,
    render: v => PartnerAccountType.__getValue(v),
  },
  {
    title: 'partnerPage_AccountOwner',
    dataIndex: 'accountOwner',
    ellipsis: true,
  },
  {
    title: 'partnerPage_AccountNumber',
    dataIndex: 'accountNumber',
    ellipsis: true,
  },
  {
    title: 'base.Active',
    dataIndex: 'isActive',
    align: 'center',
    render: v => <BooleanTag value={v} />,
  },
];
const filters: Array<IFilterItem> = [
  {
    type: 'LIST',
    label: 'partnerPage_AccountType',
    name: 'partnerAccountType',
    xlSize: 8,
    xxlSize: 8,
    mdSize: 8,
    defaultValue: PartnerAccountType.BankAccount,
    listData: [
      { label: i18n.t('partnerPage_MNOS'), value: PartnerAccountType.MobileMoneyAccount },
      { label: i18n.t('partnerPage_Bank'), value: PartnerAccountType.BankAccount },
    ],
  },
];

export default function UtilityPartnerAccountModal({ onClose, utilityPartnerId }: Props) {
  const { t } = useTranslation();
  const zTb = React.useRef<ITableRef>();

  const [form] = Form.useForm();
  const [loading, setLoading] = React.useState<boolean>(false);
  const [formVisible, setFormVisible] = React.useState(false);
  const [partnerAccount, setPartnerAccount] = React.useState<PartnerAccountDto>();
  const createNewConnectivity = (values: any) => {
    setLoading(true);
    apiSdk.PartnerAccountService.create({ body: { ...values, utilityPartnerId: utilityPartnerId } })
      .then(response => {
        if (response?.success) {
          setFormVisible(false);

          zTb.current?.reload();
        } else {
          if (response.message) {
            message.error(t(response.message || ''));
          }
        }
      })
      .catch(error => {
        message.error(error);
      })
      .finally(() => {
        setLoading(false);
      });
  };
  const updateConnectivity = (values: any) => {
    setLoading(true);
    apiSdk.PartnerAccountService.update({ body: { ...values, utilityPartnerId: utilityPartnerId } })
      .then(response => {
        if (response?.success) {
          setFormVisible(false);
          setPartnerAccount(undefined);
          zTb.current?.reload();
        } else {
          if (response.message) {
            message.error(t(response.message || ''));
          }
        }
      })
      .catch(error => {
        message.error(error);
      })
      .finally(() => {
        setLoading(false);
      });
  };
  React.useEffect(() => {
    if (partnerAccount) {
      form.setFieldsValue(partnerAccount);
      setFormVisible(true);
    }
  }, [partnerAccount]);

  const setActiveOrInActive = (id: string, active: boolean) => {
    setLoading(true);
    apiSdk.PartnerAccountService.activeInactive({
      body: {
        id: id,
        isActive: active,
      },
    })
      .then(response => {
        if (response?.success) {
          message.success(t('base.UpdateSucessMessage'));
          zTb.current?.reload();
        } else {
          if (response.message) {
            message.error(t(response.message || ''));
          }
        }
      })
      .catch(error => {
        message.error(error);
      })
      .finally(() => {
        setLoading(false);
      });
  };
  return (
    <Modal
      style={{ top: 10, minHeight: 500 }}
      visible={true}
      maskClosable={false}
      width={ModalWidth.large}
      className="nta-custom-form"
      title={t('partnerPage_UtilityPartnerAccounts')}
      forceRender={true}
      cancelText={t('Close')}
      okButtonProps={{ style: { display: 'none' } }}
      onCancel={onClose}
    >
      <ZTable<PartnerAccountDto>
        columns={columns}
        primaryKey="id"
        tableRef={zTb}
        url="/commons/partner-accounts/get-partner-accounts"
        bordered
        defaultFilter={{
          utilityPartnerId: utilityPartnerId,
          partnerAccountType: PartnerAccountType.BankAccount,
        }}
        filters={filters}
        order_by="name"
        order_by_asc={false}
        allowEdit={true}
        allowDelete={true}
        onEdit={record => {
          setPartnerAccount(record);
        }}
        delete_url="/commons/partner-accounts/delete"
        searchBtnAlignLeft
        extraActions={[
          {
            render: record => {
              return (
                <Tooltip title={t(record.isActive ? 'base.SetInActive' : 'base.SetActive')}>
                  <Button
                    loading={loading}
                    style={{ borderRadius: 0 }}
                    type="primary"
                    size="small"
                    danger={record.isActive}
                    icon={record.isActive ? <LockOutlined /> : <CheckOutlined />}
                    onClick={() => {
                      setActiveOrInActive(record?.id || '', !record.isActive);
                    }}
                  ></Button>
                </Tooltip>
              );
            },
          },
        ]}
        extraBtns={
          <Button
            key="create"
            type="primary"
            className="nta-create-btn"
            icon={<PlusCircleOutlined />}
            onClick={() => {
              form.resetFields();
              setFormVisible(true);
            }}
          >
            {t('createNew')}
          </Button>
        }
      />
      {formVisible ? (
        <Modal
          style={{ top: 10 }}
          visible={true}
          maskClosable={false}
          width={ModalWidth.small}
          className="nta-custom-form"
          title={!partnerAccount ? t('partnerPage_CreateUtilityPartnerAccount') : t('partnerPage_UpdateUtilityPartnerAccount')}
          forceRender={true}
          okText={t('save')}
          cancelText={t('cancel')}
          okButtonProps={{ icon: <SaveOutlined />, className: 'save-btn', loading: loading }}
          onCancel={() => {
            form.resetFields();
            setFormVisible(false);
            setPartnerAccount(undefined);
          }}
          onOk={() => {
            form
              .validateFields()
              .then(values => {
                if (values.id) {
                  updateConnectivity(values);
                } else {
                  createNewConnectivity(values);
                }
              })
              .catch(info => {});
          }}
        >
          <Form form={form} size="middle" layout="horizontal" labelAlign="right" labelCol={{ md: 12, xs: 24, xl: 12 }}>
            <Form.Item name="id" className="hidden">
              <Input type="hidden" />
            </Form.Item>
            <Row gutter={6}>
              <Col md={24}>
                <Form.Item label={t('partnerPage_AccountType')} name="partnerAccountType">
                  <EnumSelect enumData={PartnerAccountType} className="input-value-color" disabled={!!partnerAccount} />
                </Form.Item>
                <Form.Item label={t('partnerPage_Bank_OperatorName')} name="name">
                  <Input />
                </Form.Item>
                <Form.Item label={t('partnerPage_AccountOwner')} name="accountOwner">
                  <Input />
                </Form.Item>
                <Form.Item label={t('partnerPage_AccountNumber')} name="accountNumber">
                  <Input />
                </Form.Item>
              </Col>
            </Row>
          </Form>
        </Modal>
      ) : null}
    </Modal>
  );
}
